import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchWorkflowMetricsSummary as fetchWorkflowMetricsSummaryAPI,
  fetchWorkflowRunHistory as fetchWorkflowRunHistoryAPI,
  fetchWorkflowStepsSummary as fetchWorkflowStepsSummaryAPI,
  fetchFlowExecutionHistory as fetchFlowExecutionHistoryAPI,
  fetchWorkflowRunsById
} from '../../apis/Dashboard';

export const fetchWorkflowRunHistoryThunk = createAsyncThunk(
  'dashboard/fetchWorkflowRunHistory',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchWorkflowRunHistoryAPI(params);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchWorkflowMetricsSummaryThunk = createAsyncThunk(
  'dashboard/fetchWorkflowMetricsSummary',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchWorkflowMetricsSummaryAPI();
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchWorkflowStepsSummaryThunk = createAsyncThunk(
  'dashboard/fetchWorkflowStepsSummary',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchWorkflowStepsSummaryAPI(params);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchFlowExecutionHistoryThunk = createAsyncThunk(
  'dashboard/fetchFlowExecutionHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await fetchFlowExecutionHistoryAPI(params); // Pass params to API
      return data?.workflows || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchWorkflowRunsByIdThunk = createAsyncThunk(
  'dashboard/fetchWorkflowRunsById',
  async (workflowId, { rejectWithValue }) => {
    try {
      const data = await fetchWorkflowRunsById(workflowId);
      return data?.runs || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  workflowRunHistory: [],
  loading: false,
  error: null,
  metricsSummary: null,
  metricsSummaryLoading: false,
  metricsSummaryError: null,
  stepsSummary: null,
  stepsSummaryLoading: false,
  stepsSummaryError: null,
  flowExecutionData: [],
  flowExecutionLoading: false,
  flowExecutionError: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflowRunHistoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowRunHistoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.workflowRunHistory = action.payload || [];
        state.error = null;
      })
      .addCase(fetchWorkflowRunHistoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWorkflowMetricsSummaryThunk.pending, (state) => {
        state.metricsSummaryLoading = true;
        state.metricsSummaryError = null;
      })
      .addCase(fetchWorkflowMetricsSummaryThunk.fulfilled, (state, action) => {
        state.metricsSummaryLoading = false;
        state.metricsSummary = action.payload || null;
        state.metricsSummaryError = null;
      })
      .addCase(fetchWorkflowMetricsSummaryThunk.rejected, (state, action) => {
        state.metricsSummaryLoading = false;
        state.metricsSummaryError = action.payload;
      })
      .addCase(fetchWorkflowStepsSummaryThunk.pending, (state) => {
        state.stepsSummaryLoading = true;
        state.stepsSummaryError = null;
      })
      .addCase(fetchWorkflowStepsSummaryThunk.fulfilled, (state, action) => {
        state.stepsSummaryLoading = false;
        state.stepsSummary = action.payload || null;
        state.stepsSummaryError = null;
      })
      .addCase(fetchWorkflowStepsSummaryThunk.rejected, (state, action) => {
        state.stepsSummaryLoading = false;
        state.stepsSummaryError = action.payload;
      })
      .addCase(fetchFlowExecutionHistoryThunk.pending, (state) => {
        state.flowExecutionLoading = true;
        state.flowExecutionError = null;
      })
      .addCase(fetchFlowExecutionHistoryThunk.fulfilled, (state, action) => {
        state.flowExecutionLoading = false;
        state.flowExecutionError = null;
        state.flowExecutionData = action.payload || [];
      })
      .addCase(fetchFlowExecutionHistoryThunk.rejected, (state, action) => {
        state.flowExecutionLoading = false;
        state.flowExecutionError = action.payload;
      })
      .addCase(fetchWorkflowRunsByIdThunk.pending, (state) => {
      state.workflowRunsLoading = true;
      state.workflowRunsError = null;
    })
    .addCase(fetchWorkflowRunsByIdThunk.fulfilled, (state, action) => {
      state.workflowRunsLoading = false;
      state.workflowRuns = action.payload;
    })
    .addCase(fetchWorkflowRunsByIdThunk.rejected, (state, action) => {
      state.workflowRunsLoading = false;
      state.workflowRunsError = action.payload;
    });
  },
});

export default dashboardSlice.reducer;
